import { useState, useCallback } from 'react';

import {
  Box,
  Card,
  Stack,
  Alert,
  Button,
  TextField,
  Typography,
  CardContent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Iconify } from 'src/components/iconify';
import { changePassword } from 'src/api/user.api';

// ----------------------------------------------------------------------

export function SettingsView() {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = useCallback((field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setError(null);
    setSuccess(false);
  }, []);

  const validateForm = useCallback((): string | null => {
    if (!formData.oldPassword) {
      return 'Mevcut sifre gereklidir';
    }
    if (!formData.newPassword) {
      return 'Yeni sifre gereklidir';
    }
    if (formData.newPassword.length < 8) {
      return 'Sifre en az 8 karakter olmalidir';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return 'Sifreler eslesmiyor';
    }
    if (formData.oldPassword === formData.newPassword) {
      return 'Yeni sifre eski sifreden farkli olmalidir';
    }
    return null;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await changePassword(formData.oldPassword, formData.newPassword);
      setSuccess(true);
      setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      setError(err.message || 'Sifre degistirme basarisiz oldu');
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 5 }}>
        Ayarlar
      </Typography>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Sifre Degistir
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Sifreniz basariyla degistirildi. Lutfen yeni sifrenizle giris yapin.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                required
                label="Mevcut Sifre"
                type={showOldPassword ? 'text' : 'password'}
                value={formData.oldPassword}
                onChange={handleChange('oldPassword')}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        <Iconify icon={showOldPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="Yeni Sifre"
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange('newPassword')}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        <Iconify icon={showNewPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Sifre en az 8 karakter olmalidir"
              />

              <TextField
                fullWidth
                required
                label="Yeni Sifre (Tekrar)"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        <Iconify icon={showConfirmPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                    setError(null);
                    setSuccess(false);
                    setShowOldPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  disabled={loading}
                >
                  Iptal
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={loading}
                >
                  Sifreyi Degistir
                </LoadingButton>
              </Box>
            </Stack>
          </Box>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Guvenlik:</strong> Sifre degisikligi icin mevcut sifreniz dogrulanir.
              Tum kullanicilar (MANAGER, ADMIN, SUPER_ADMIN) guvenli bir sekilde sifrelerini degistirebilir.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}
